// Customer Assets tab — read-only view of a customer's GCS kits and the drones
// recently connected to each kit's VPN IP, plus the flat list of drones whose
// customer_org_id currently points at this customer.
//
// Mutations (Assign / Sold / Clear) are in CustomerAssetsTabActions and are
// gated to can_admin — wired separately so this read-only surface can ship and
// be used by ops while the mutation flow gets its loud-confirmation review.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import { buttonClass } from '../components/ui/Button';
import type { ButtonVariant } from '../components/ui/Button';
import { createColumnHelper } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '../components/table/DataTable';
import Dialog from '../components/ui/Dialog';
import EmptyState from '../components/ui/EmptyState';
import Panel from '../components/ui/Panel';
import Reveal from '../components/ui/Reveal';
import Skeleton, { SkeletonGroup } from '../components/ui/Skeleton';
import Tag from '../components/ui/Tag';
import type { TagTone } from '../components/ui/Tag';
import { CATEGORY } from '../components/ui/categoryColors';
import { formatTimestamp } from '../utils/date-utils';
import FlameIcon from '../components/FlameIcon';
import useUserStore from '../stores/useUserStore';

interface RecentlyConnectedDrone {
  serialnumber: string;
  server: string;
  last_seen: string;
  title: string | null;
  customer_org_id: number | null;
  customer_org_name: string | null;
  is_internal_pool: boolean;
  pyro_enabled: boolean;
}

interface GcsKit {
  asset_id: string;
  display_code: string;
  operational_status: string;
  notes: string | null;
  vpn_ip: string | null;
  last_telemetry_at: string | null;
  recently_connected_drones: RecentlyConnectedDrone[];
}

interface AssignedDrone {
  serialnumber: string;
  title: string | null;
  customer_org_id: number | null;
  customer_org_name: string | null;
  is_internal_pool: boolean;
  pyro_enabled: boolean;
  last_seen_time: string | null;
  last_seen_server: string | null;
}

interface CustomerAssetsResponse {
  gcs_kits: GcsKit[];
  assigned_drones: AssignedDrone[];
  connected_window_months: number;
}

const STATUS_TONE: Record<string, TagTone> = {
  ready: 'good',
  building: 'warn',
  flagged: 'warn',
  ooc: 'bad',
  checked_out: 'info',
  retired: 'neutral',
};

const kitHelper = createColumnHelper<RecentlyConnectedDrone>();
const assignedHelper = createColumnHelper<AssignedDrone>();

const assignedColumns: ColumnDef<AssignedDrone, any>[] = [
  assignedHelper.accessor('serialnumber', {
    header: 'Serial',
    cell: i => <Link to={`/drone/${i.getValue()}`} className="font-mono text-on-surface hover:text-primary">{i.getValue()}</Link>,
  }),
  assignedHelper.accessor('is_internal_pool', {
    header: 'Ownership',
    cell: i => (
      <span className="text-[11px] font-medium" style={{ color: (i.getValue() ? CATEGORY.blue : CATEGORY.green).ink.oklch }}>
        {i.getValue() ? 'Leased (Firefly retains)' : 'Sold to customer'}
      </span>
    ),
  }),
  assignedHelper.accessor('pyro_enabled', {
    header: 'Pyro',
    meta: { align: 'center' },
    cell: i => (i.getValue() ? <FlameIcon className="w-3.5 h-3.5 inline-block" /> : <span className="text-on-surface-variant">—</span>),
  }),
  assignedHelper.accessor('last_seen_server', {
    header: 'Last Server',
    cell: i => <span className="font-mono text-on-surface-variant">{i.getValue() ?? <span className="text-on-surface-variant">—</span>}</span>,
  }),
  assignedHelper.accessor('last_seen_time', {
    header: 'Last Seen',
    meta: { align: 'right' },
    cell: i => (
      <span className="font-mono text-on-surface-variant">
        {i.getValue() ? formatTimestamp(i.getValue() as string, 'MMM D, HH:mm') : <span>—</span>}
      </span>
    ),
  }),
];

const OwnerBadge: React.FC<{ orgId: number | null; orgName: string | null; isInternalPool: boolean; thisCustomerId: number }> = ({ orgId, orgName, isInternalPool, thisCustomerId }) => {
  if (orgId === null) {
    return <span className="text-[11px] italic" style={{ color: CATEGORY.amber.ink.oklch }}>Firefly Internal</span>;
  }
  if (orgId === thisCustomerId) {
    const label = isInternalPool ? 'Leased to this customer' : 'Sold to this customer';
    const color = (isInternalPool ? CATEGORY.blue : CATEGORY.green).ink.oklch;
    return <span className="text-[11px] font-medium" style={{ color }}>{label}</span>;
  }
  return <span className="text-[11px] italic text-on-surface-variant">{orgName ?? `Org #${orgId}`}</span>;
};

type PendingAction =
  | { kind: 'assign'; gcsAssetId: string; gcsCode: string; leaseOrSale: 'lease' | 'sale'; drones: RecentlyConnectedDrone[] }
  | { kind: 'clear' };

const CustomerAssetsTab: React.FC<{ customerId: number }> = ({ customerId }) => {
  const api = useApi();
  const toast = useToast();
  const hasCapability = useUserStore(s => s.hasCapability);
  const canAdmin = hasCapability('admin');
  const [data, setData] = useState<CustomerAssetsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  // Drones list defaults to collapsed; large customers (e.g. Pyrotecnico has ~1k rows)
  // make page scroll painful when expanded by default.
  const [dronesOpen, setDronesOpen] = useState(false);
  // Per-GCS "recently connected" expand state — Set of asset_ids the user has opened.
  // Default empty = all collapsed; operator drills into one GCS at a time.
  const [expandedGcs, setExpandedGcs] = useState<Set<string>>(new Set());
  const toggleGcs = (id: string) => {
    setExpandedGcs(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  // Confirmation modal state — null when no action is pending.
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/customers/${customerId}/assets`);
      setData(res);
    } catch (e: any) {
      toast.error(e.message || 'Failed to load assets');
    } finally {
      setLoading(false);
    }
    // api and toast are stable refs; intentionally excluded to avoid loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const submitPendingAction = useCallback(async () => {
    if (!pendingAction) return;
    setSubmitting(true);
    try {
      if (pendingAction.kind === 'assign') {
        const res = await api.post(`/customers/${customerId}/assign-recent-drones`, {
          body: {
            asset_id: pendingAction.gcsAssetId,
            lease_or_sale: pendingAction.leaseOrSale,
            drone_serials: pendingAction.drones.map(d => d.serialnumber),
          },
        });
        const verb = pendingAction.leaseOrSale === 'lease' ? 'leased to' : 'sold to';
        toast.success(`${res.updated.length} drone${res.updated.length === 1 ? '' : 's'} ${verb} ${res.customer_name}${res.unchanged.length ? ` (${res.unchanged.length} unchanged)` : ''}`);
      } else if (pendingAction.kind === 'clear') {
        const res = await api.post(`/customers/${customerId}/clear-drones`, { body: {} });
        toast.success(`Cleared ${res.cleared.length} drone${res.cleared.length === 1 ? '' : 's'} from ${res.customer_name}`);
      }
      setPendingAction(null);
      await load();
    } catch (e: any) {
      toast.error(e.message || 'Action failed');
    } finally {
      setSubmitting(false);
    }
    // api/toast are stable refs; intentionally excluded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAction, customerId, load]);

  const kitColumns = useMemo<ColumnDef<RecentlyConnectedDrone, any>[]>(() => [
    kitHelper.accessor('serialnumber', {
      header: 'Serial',
      cell: i => (
        <Link to={`/drone/${i.getValue()}`} className="font-mono text-on-surface hover:text-primary">{i.getValue()}</Link>
      ),
    }),
    kitHelper.accessor('pyro_enabled', {
      header: 'Pyro',
      meta: { align: 'center' },
      cell: i => (i.getValue() ? <FlameIcon className="w-3.5 h-3.5 inline-block" /> : <span className="text-on-surface-variant">—</span>),
    }),
    kitHelper.display({
      id: 'owner',
      header: 'Current Owner',
      cell: i => {
        const d = i.row.original;
        return <OwnerBadge orgId={d.customer_org_id} orgName={d.customer_org_name} isInternalPool={d.is_internal_pool} thisCustomerId={customerId} />;
      },
    }),
    kitHelper.accessor('last_seen', {
      header: 'Last Seen',
      meta: { align: 'right' },
      cell: i => <span className="font-mono text-on-surface-variant">{formatTimestamp(i.getValue(), 'MMM D, HH:mm')}</span>,
    }),
  ], [customerId]);

  if (loading) {
    return (
      <Panel label="Loading assets">
        <SkeletonGroup label="Loading assets" className="space-y-3" >
          <Skeleton className="h-5 w-1/3" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </SkeletonGroup>
      </Panel>
    );
  }

  if (!data) return null;

  const totalDrones = data.assigned_drones.length;
  const pyroCount = data.assigned_drones.filter(d => d.pyro_enabled).length;

  return (
    <div className="space-y-5">
      {/* GCS Kits */}
      <Panel
        title="Ground Station Kits"
        actions={
          <span className="text-[11px] text-on-surface-variant uppercase tracking-widest">
            {data.gcs_kits.length} kit{data.gcs_kits.length === 1 ? '' : 's'}
          </span>
        }
      >
        {data.gcs_kits.length === 0 ? (
          <EmptyState variant="area">No GCS kits assigned to this customer.</EmptyState>
        ) : (
          <div className="space-y-6">
            {data.gcs_kits.map(gcs => {
              const expanded = expandedGcs.has(gcs.asset_id);
              return (
              <div key={gcs.asset_id}>
                <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
                  <div className="flex items-baseline gap-3">
                    <Link to={`/assets/${gcs.asset_id}`} className="font-mono text-on-surface hover:text-primary text-base font-semibold">
                      {gcs.display_code}
                    </Link>
                    {gcs.vpn_ip && (
                      <span className="font-mono text-xs" style={{ color: CATEGORY.amber.ink.oklch }}>{gcs.vpn_ip}</span>
                    )}
                    <Tag size="sm" tone={STATUS_TONE[gcs.operational_status] || 'neutral'} className="capitalize">
                      {gcs.operational_status.replace('_', ' ')}
                    </Tag>
                  </div>
                  {gcs.last_telemetry_at && (
                    <span className="text-[11px] text-on-surface-variant font-mono">
                      Last telemetry: {formatTimestamp(gcs.last_telemetry_at, 'MMM D, YYYY HH:mm')}
                    </span>
                  )}
                </div>

                {/* Recently connected drones for this GCS */}
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => toggleGcs(gcs.asset_id)}
                    className="w-full flex items-center justify-between mb-2 group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded"
                    aria-expanded={expanded}
                    aria-controls={`gcs-connected-${gcs.asset_id}`}
                  >
                    <div className="flex items-center gap-1.5">
                      <svg
                        aria-hidden="true"
                        className={`w-3.5 h-3.5 text-on-surface-variant transition-transform duration-150 ${expanded ? 'rotate-90' : ''}`}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                      <span className="text-[11px] text-on-surface-variant uppercase tracking-widest group-hover:text-on-surface">
                        Recently connected ({data.connected_window_months} mo)
                      </span>
                    </div>
                    <span className="text-[10px] text-on-surface-variant font-mono">
                      {gcs.recently_connected_drones.length} drone{gcs.recently_connected_drones.length === 1 ? '' : 's'}
                    </span>
                  </button>
                  <div id={`gcs-connected-${gcs.asset_id}`}>
                  <Reveal show={expanded}>
                  {gcs.recently_connected_drones.length === 0 ? (
                    <EmptyState variant="line" dense>No drones connected in this window.</EmptyState>
                  ) : (
                    <DataTable
                      data={gcs.recently_connected_drones}
                      columns={kitColumns}
                      getRowId={d => d.serialnumber}
                      on="panel"
                      density="compact"
                      empty={null}
                    />
                  )}
                  {canAdmin && gcs.recently_connected_drones.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-800/60 flex flex-wrap gap-2 justify-end">
                      <button
                        type="button"
                        onClick={() => setPendingAction({
                          kind: 'assign',
                          gcsAssetId: gcs.asset_id,
                          gcsCode: gcs.display_code,
                          leaseOrSale: 'lease',
                          drones: gcs.recently_connected_drones,
                        })}
                        className={buttonClass('info', 'sm')}
                      >
                        Assign as Lease
                      </button>
                      <button
                        type="button"
                        onClick={() => setPendingAction({
                          kind: 'assign',
                          gcsAssetId: gcs.asset_id,
                          gcsCode: gcs.display_code,
                          leaseOrSale: 'sale',
                          drones: gcs.recently_connected_drones,
                        })}
                        className={buttonClass('good', 'sm')}
                      >
                        Mark Sold
                      </button>
                    </div>
                  )}
                  </Reveal>
                  </div>{/* end gcs-connected wrapper */}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </Panel>

      {/* Currently assigned drones */}
      <Panel>
        <Reveal show={!!canAdmin && totalDrones > 0}>
          <div className="flex justify-end mb-3">
            <button
              type="button"
              onClick={() => setPendingAction({ kind: 'clear' })}
              className={buttonClass('danger-quiet', 'sm')}
            >
              Clear Drones from Customer
            </button>
          </div>
        </Reveal>
        <button
          type="button"
          onClick={() => setDronesOpen(o => !o)}
          className="w-full flex items-center justify-between mb-4 group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded"
          aria-expanded={dronesOpen}
          aria-controls="customer-assigned-drones"
        >
          <div className="flex items-center gap-2">
            <svg
              aria-hidden="true"
              className={`w-4 h-4 text-on-surface-variant transition-transform duration-150 ${dronesOpen ? 'rotate-90' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
            <h2 className="text-base font-semibold text-on-surface group-hover:text-white">Currently Assigned Drones</h2>
          </div>
          <span className="text-[11px] text-on-surface-variant uppercase tracking-widest">
            {totalDrones} drone{totalDrones === 1 ? '' : 's'}
            {pyroCount > 0 && <span className="ml-2">· {pyroCount} pyro</span>}
          </span>
        </button>

        <div id="customer-assigned-drones">
        <Reveal show={dronesOpen}>
        {totalDrones === 0 ? (
          <EmptyState variant="area">No drones currently linked to this customer.</EmptyState>
        ) : (
          <DataTable
            data={data.assigned_drones}
            columns={assignedColumns}
            getRowId={d => d.serialnumber}
            on="panel"
            density="compact"
            empty={null}
          />
        )}
        </Reveal>
        </div>{/* end customer-assigned-drones wrapper */}
      </Panel>

      {pendingAction && (
        <ConfirmModal
          action={pendingAction}
          customerId={customerId}
          totalAssignedDrones={totalDrones}
          submitting={submitting}
          onCancel={() => setPendingAction(null)}
          onConfirm={submitPendingAction}
        />
      )}
    </div>
  );
};

// Loud confirmation dialog: destructive actions get the danger title. Counts what will actually change so ops
// can sanity-check before committing.
const ConfirmModal: React.FC<{
  action: PendingAction;
  customerId: number;
  totalAssignedDrones: number;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ action, customerId, totalAssignedDrones, submitting, onCancel, onConfirm }) => {
  let title = '';
  let body: React.ReactNode = null;
  let confirmVariant: ButtonVariant = 'filled';
  let confirmLabel = '';

  if (action.kind === 'clear') {
    title = 'Clear Drones from Customer';
    confirmVariant = 'danger';
    confirmLabel = 'Clear Ownership';
    body = (
      <p className="text-sm text-on-surface">
        Return <span className="font-semibold text-white">{totalAssignedDrones}</span> drone{totalAssignedDrones === 1 ? '' : 's'} to <span className="italic" style={{ color: CATEGORY.amber.ink.oklch }}>Firefly Internal</span>.
        <br />
        <span className="text-on-surface-variant text-xs">Pyro flags are preserved. This cannot be automatically undone.</span>
      </p>
    );
  } else {
    const targetIsLease = action.leaseOrSale === 'lease';
    const targetIsInternalPool = targetIsLease;
    // Count drones that will change vs. those already in the requested state.
    const wouldChange = action.drones.filter(d => !(d.customer_org_id === customerId && d.is_internal_pool === targetIsInternalPool));
    const alreadyMatch = action.drones.length - wouldChange.length;

    title = targetIsLease ? 'Assign as Lease' : 'Mark as Sold';
    confirmLabel = targetIsLease ? 'Assign Lease' : 'Mark Sold';

    body = (
      <div className="text-sm text-on-surface space-y-1">
        <p>
          {targetIsLease ? 'Lease' : 'Transfer ownership of'} <span className="font-semibold text-white">{wouldChange.length}</span> drone{wouldChange.length === 1 ? '' : 's'} from GCS <span className="font-mono" style={{ color: CATEGORY.amber.ink.oklch }}>{action.gcsCode}</span>.
        </p>
        {alreadyMatch > 0 && (
          <p className="text-xs text-on-surface-variant">
            {alreadyMatch} drone{alreadyMatch === 1 ? '' : 's'} already in the target state — unchanged.
          </p>
        )}
        <p className="text-xs text-on-surface-variant">
          Each changed drone gets a maintenance log entry. Existing ownership is overwritten.
        </p>
      </div>
    );
  }

  return (
    <Dialog
      open
      alert
      onClose={onCancel}
      title={title}
      tone={action.kind === 'clear' ? 'danger' : 'default'}
      dismissible={action.kind !== 'clear'}
      busy={submitting}
      footer={
        <>
          <button type="button" onClick={onCancel} disabled={submitting} className={buttonClass('quiet', 'md')}>
            Cancel
          </button>
          <button type="button" onClick={onConfirm} disabled={submitting} className={buttonClass(confirmVariant, 'md')}>
            {submitting ? 'Working…' : confirmLabel}
          </button>
        </>
      }
    >
      {body}
    </Dialog>
  );
};

export default CustomerAssetsTab;
